import { modals } from '@mantine/modals';
import { snapshot } from 'valtio';

import { SelectionService } from '@diagram/services/SelectionService';
import { SidebarHistoryService } from '@diagram/services/SidebarHistoryService';
import { state as modeState } from '@diagram/state/mode';
import type { SelectionItem } from '@diagram/state/selection';
import {
    EDIT_SIDEBAR_TYPES,
    LOCAL_STORAGE_KEY,
    reset,
    SidebarConfirmModal,
    SidebarTab,
    SidebarType,
    state as sidebarState,
    TABBED_SIDEBAR_TYPES,
} from '@diagram/state/sidebar';

import {
    PermissionDiagramElements,
    PermissionDiagramFiles,
    PermissionDiagramGeneral,
    PermissionDiagramSimulations,
    PermissionDigramComments,
} from 'models';

import { LocalStorageService } from 'services/LocalStorageService';
import { PermissionService } from 'services/PermissionService';
import { state as currentProjectState } from 'state/current-project';

const { can } = PermissionService;

type StoredSidebar = {
    type: SidebarType;
    tab?: SidebarTab;
    collapsed: boolean;
    selection: SelectionItem[];
};

type LastSidebarMap = Record<string, StoredSidebar>;

const SidebarService = {
    snapshot: () => {
        return snapshot(sidebarState);
    },

    getShowSidebar: () => {
        return !sidebarState.collapsed;
    },

    getFallbackType: (): SidebarType => {
        if (can(PermissionDiagramElements.CREATE)) {
            return SidebarType.ADD;
        }

        return SidebarType.GETTING_STARTED;
    },

    open: (type: SidebarType, tab?: SidebarTab) => {
        if (sidebarState.confirmModal) {
            modals.openConfirmModal({
                centered: true,
                ...sidebarState.confirmModal,

                onConfirm: async () => {
                    await sidebarState.confirmModal?.onConfirm?.();
                    SidebarService.setConfirmModal(null);
                    SidebarService.open(type, tab);
                },

                onCancel: async () => {
                    await sidebarState.confirmModal?.onCancel?.();
                    SidebarService.setConfirmModal(null);
                    SidebarService.open(type, tab);
                },
            });

            return;
        }

        const currentSidebar = SidebarService.getFallbackType();

        const setType = (hasPermission: boolean) => {
            if (hasPermission) {
                sidebarState.type = type;

                if (tab) {
                    sidebarState.tab = tab;
                } else if (!TABBED_SIDEBAR_TYPES.includes(type)) {
                    sidebarState.tab = null;
                }

                return;
            }

            sidebarState.type = currentSidebar;
        };

        if (modeState.mode === 'comment') {
            modeState.mode = can(PermissionDiagramElements.EDIT) ? 'edit' : 'navigate';
        }

        switch (type) {
            case SidebarType.GETTING_STARTED:
                setType(true);
                break;

            case SidebarType.ADD:
            case SidebarType.SNIPPETS:
                setType(can(PermissionDiagramElements.CREATE));
                break;

            case SidebarType.LIVE_DATA:
                setType(can(PermissionDiagramGeneral.EDIT));
                break;

            case SidebarType.EDIT_COMPONENT:
            case SidebarType.EDIT_COMPONENT_INSTANCES:
            case SidebarType.EDIT_CONNECTION:
            case SidebarType.EDIT_CONNECTIONS:
            case SidebarType.EDIT_INFO_BLOCK:
                setType(true);
                break;

            case SidebarType.AI:
                setType(can(PermissionDiagramGeneral.AI));
                break;

            case SidebarType.COMMENTS:
                if (can(PermissionDigramComments.VIEW)) {
                    modeState.mode = 'comment';
                    setType(true);
                }
                break;

            case SidebarType.CHAT:
                setType(can(PermissionDigramComments.VIEW));
                break;

            case SidebarType.BILL_OF_MATERIALS:
                setType(can(PermissionDiagramGeneral.BOM));
                break;

            case SidebarType.SHARE:
                setType(can(PermissionDiagramGeneral.SHARE) || currentProjectState.project?.permissionsAs === 'user');
                break;

            case SidebarType.PROJECT:
                setType(can(PermissionDiagramGeneral.EDIT));
                break;

            case SidebarType.FILES:
                setType(can(PermissionDiagramFiles.VIEW));
                break;

            case SidebarType.WIRE_SCHEDULE:
                setType(true);
                break;

            case SidebarType.SIMULATIONS:
            case SidebarType.FAULT_SIMULATIONS:
            case SidebarType.SIMULATIONS_CREATE:
            case SidebarType.SIMULATION_PROFILES:
            case SidebarType.VALIDATIONS:
            case SidebarType.BATCH_SIMULATION_GRAPH:
                setType(can(PermissionDiagramSimulations.VIEW));
                break;
        }

        sidebarState.collapsed = false;
    },

    close: (type: SidebarType) => {
        if (sidebarState.type === type) {
            SidebarService.openDefaultSidebar();
        }
    },

    closeEditSidebar: () => {
        if (EDIT_SIDEBAR_TYPES.includes(sidebarState.type)) {
            SidebarService.openDefaultSidebar();
        }
    },

    toggleCollapsed: () => {
        sidebarState.collapsed = !sidebarState.collapsed;
    },

    init: (projectId: string) => {
        const stored = SidebarService.getSavedStateForProject(projectId);

        SidebarHistoryService.reset();
        reset();

        if (stored) {
            SidebarService.open(stored.type, stored.tab);
            sidebarState.collapsed = stored.collapsed;

            if (stored.selection?.length) {
                SelectionService.select(stored.selection.map((item) => ({ ...item })));
            }
        }
    },

    openDefaultSidebar: () => {
        SidebarService.open(SidebarService.getFallbackType());
    },

    setActiveTab: (tab: SidebarTab) => {
        sidebarState.tab = tab;
    },

    setWidth: (width: number) => {
        const previous = sidebarState.width;

        sidebarState.width = width;

        return () => {
            sidebarState.width = previous;
        };
    },

    reset: () => reset(),

    setConfirmModal: (confirmModal: SidebarConfirmModal | null) => {
        sidebarState.confirmModal = confirmModal;
    },

    highlightSection: (section: string) => {
        LocalStorageService.store('highlighted-sidebar-section', section);
    },

    getHighlightedSection: () => {
        return LocalStorageService.get('highlighted-sidebar-section') || '';
    },

    clearHighlightedSection: () => {
        LocalStorageService.clear('highlighted-sidebar-section');
    },

    getCollapsableSections: () => {
        return LocalStorageService.get('collapsable-sections') || {};
    },

    getCollapsableSectionState: (key: string) => {
        return SidebarService.getCollapsableSections()[key];
    },

    setCollapsableSectionState: (key: string, state: boolean) => {
        const collapsableSections = SidebarService.getCollapsableSections();

        collapsableSections[key] = state;

        LocalStorageService.store('collapsable-sections', collapsableSections);
    },

    getSavedState: () => {
        return LocalStorageService.get<LastSidebarMap>(LOCAL_STORAGE_KEY) || {};
    },

    getSavedStateForProject: (projectId: string) => {
        return SidebarService.getSavedState()[projectId] ?? null;
    },
};

export { SidebarService };
